import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { extractErrorMessage } from '../utils/apiFetch';

const validationSchema = yup.object({
  email: yup.string().email('Ungültige E-Mail').required('E-Mail ist erforderlich'),
  username: yup.string().min(3, 'Benutzername muss mindestens 3 Zeichen lang sein').required('Benutzername ist erforderlich'),
  password: yup.string().min(8, 'Passwort muss mindestens 8 Zeichen lang sein').required('Passwort ist erforderlich'),
  confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwörter müssen übereinstimmen').required('Passwort bestätigen ist erforderlich'),
  fullName: yup.string(),
});

const RegisterPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      fullName: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);

      try {
        await register({
          email: values.email,
          username: values.username,
          password: values.password,
          full_name: values.fullName || undefined,
        });
        navigate('/onboarding');
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.'));
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <Helmet>
        <title>Registrieren - btrader</title>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom textAlign="center">
              Registrieren
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              Erstellen Sie Ihr btrader-Konto
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={formik.handleSubmit}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="E-Mail"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                margin="normal"
                autoComplete="email"
              />
              <TextField
                fullWidth
                id="username"
                name="username"
                label="Benutzername"
                value={formik.values.username}
                onChange={formik.handleChange}
                error={formik.touched.username && Boolean(formik.errors.username)}
                helperText={formik.touched.username && formik.errors.username}
                margin="normal"
                autoComplete="username"
              />
              <TextField
                fullWidth
                id="fullName"
                name="fullName"
                label="Vollständiger Name (Optional)"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                helperText={formik.touched.fullName && formik.errors.fullName}
                margin="normal"
                autoComplete="name"
              />
              <TextField
                fullWidth
                id="password"
                name="password"
                label="Passwort"
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                margin="normal"
                autoComplete="new-password"
              />
              <TextField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label="Passwort bestätigen"
                type="password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                margin="normal"
                autoComplete="new-password"
              />
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={loading}
                sx={{ mt: 3, mb: 2, height: 48 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Registrieren'}
              </Button>
            </form>

            <Box textAlign="center">
              <Link component={RouterLink} to="/login" variant="body2">
                Bereits ein Konto? Anmelden
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default RegisterPage;